import React, { ReactElement, ReactNode, useState } from "react";
import { useGetChannelTagsQuery } from "../../../__generated__/api";
import DisplayChannelTag from "../../creators/channel-tag/ChannelTag";
import { FilterPropCategory, Filters } from "../../filters/Filters";
import Select, { Option, OptionWithChildren, SelectInstanceHandler } from "../../select/Select";

export interface ChannelTagsPickerProps<TIsMulti = false, TIsClearable=false> {
    isMulti?: TIsMulti;
    minRequired?: number;
    placeholder?: string;
    canCreate?: boolean;
    className?: string;
    isClearable?: TIsClearable;
    shouldShowDropdown?: boolean;
    icon?: ReactNode;
    searchMinimum?: number;
    canSelect?: boolean;
    toggleClassName?: string;
    toggleText?: string | ReactElement

    value: (TIsMulti extends true ? string[] : string) | undefined;
    onSelected: (sourceId: (TIsMulti extends true ? string[] : string)) => void;
}

const ChannelTags = React.forwardRef(<TIsMulti extends boolean = false, TIsClearable extends boolean = false>({
    isMulti, onSelected, placeholder, minRequired, value, canCreate, className,
    shouldShowDropdown, toggleClassName, toggleText, isClearable, icon, searchMinimum, canSelect,
}: ChannelTagsPickerProps<TIsMulti, TIsClearable>, ref: React.ForwardedRef<SelectInstanceHandler>): ReactElement => {
    const { data, loading, error } = useGetChannelTagsQuery();

    const [isShowCreate, setIsShowCreate] = useState(false);

    if (loading || error) {
        return (
            <div>Loading...</div>
        );
    }

    type SelectedType = TIsMulti extends true ? readonly Option<string>[] : Option<string>;
    type ValueType = TIsMulti extends true ? Option<string>[] : Option<string>;

    const handleSelected = (selected: SelectedType) => {
        let values = (Array.isArray(selected)
            ? selected.map(row => row.value as string)
            : (selected as Option<string>).value) as Parameters<typeof onSelected>[0];

        if (values === "new" || (Array.isArray(values) && values.includes("new"))) {
            setIsShowCreate(true);
            if (Array.isArray(values)) {
                values = values.filter(row => row !== "new") as typeof values;
            } else {
                return;
            }
        }

        onSelected(values);
    };

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const values = data?.settings?.channels?.channelTags?.map(tag => ({
        label: tag,
        value: tag,
    })) ?? [];
    const valueMap = values.toIdMap("value");
    const selected = Array.isArray(value) ? value.map(row => valueMap[row]) : valueMap[value as string];

    let options: (Option<string> | OptionWithChildren<string>)[] = values;

    let control: ReactElement;
    if (shouldShowDropdown) {
        const categories: FilterPropCategory[] = options.map(row => ({
            value: (row as Option<string>).value as string,
            label: row.label as string,
        }));
        if (shouldShowDropdown) {
            categories.push({
                isDivider: true,
            });
            categories.push({
                label: "Create new channel tag",
                value: "new",
            });
        }
        control = (
            <Filters
                toggleText={toggleText}
                isMulti={isMulti}
                categories={categories}
                className={className}
                toggleClassName={toggleClassName}
                searchMinimum={searchMinimum}
                icon={icon}
                canSelect={canSelect}
                selectedCategories={
                    (isMulti ? new Set(value) : (value as string)) as TIsMulti extends true ? Set<string> : string
                }
                onSelected={current => {
                    if (isMulti) {
                        (handleSelected as ((value: readonly Option<string>[]) => void))(
                            Array.from(current).map(row => ({
                                label: row,
                                value: row,
                            })),
                        );
                    } else {
                        (handleSelected as ((value: Option<string>) => void))({
                            label: current as string,
                            value: current as string,
                        });
                    }
                }}
            />
        );
    } else {
        if (canCreate) {
            options = [
                {
                    label: "Existing Channel tags",
                    options: values,
                },
                {
                    label: "Create",
                    options: [
                        {
                            label: "Create new channel tag",
                            value: "new",
                        },
                    ],
                },
            ];
        }
        control = (
            <Select
                ref={ref}
                minRequired={minRequired}
                placeholder={placeholder}
                isMulti={isMulti}
                onChange={handleSelected}
                options={(options ?? []) as Option<string>[]}
                value={selected as ValueType}
                className={className}
                isClearable={isClearable}
            />
        );
    }

    return (
        <>
            {isShowCreate && (
                <DisplayChannelTag
                    isModal
                    onHide={() => setIsShowCreate(false)}
                    onSave={tag => {
                        // stuff

                        if (isMulti && Array.isArray(selected)) {
                            const copy = selected.toIdList(row => row.value);
                            copy.push(tag);
                            onSelected(copy as Parameters<typeof onSelected>[0]);
                        } else {
                            onSelected(tag as Parameters<typeof onSelected>[0]);
                        }

                        setIsShowCreate(false);
                    }}
                />
            )}
            {control}

        </>
    );
});

ChannelTags.defaultProps = {
    isMulti: false,
    minRequired: undefined,
    placeholder: undefined,
    canCreate: undefined,
    className: undefined,
    toggleText: undefined,
    shouldShowDropdown: false,
    toggleClassName: undefined,
    isClearable: undefined,
    icon: undefined,
    searchMinimum: undefined,
    canSelect: false,
};

export default ChannelTags as <
    TMulti extends boolean = false,
    TIsClearable extends boolean = false
> (
    props: ChannelTagsPickerProps<TMulti, TIsClearable> & { ref?: React.ForwardedRef<SelectInstanceHandler> }
) => ReturnType<typeof ChannelTags>;
